<template>
  <main-drawer
    :title="isEdit ? $tc('tournament.edit_team') : $tc('tournament.add_team')"
    @close="$emit('close')"
  >
    <main-select
      v-if="showClubSelect"
      v-model="form.clubId"
      :label="$tc('general.club')"
      :options="clubOptions"
      @input="onClubChange"
    />

    <main-select
      v-model="form.category"
      :label="$tc('settings.category')"
      :options="categoryOptions"
      :validator="$v.form.category"
    />

    <template v-if="form.category">
      <main-select
        v-model="form.disciplines[form.category]"
        :validator="
          $v.form.disciplines ? $v.form.disciplines[form.category] : null
        "
        :label="`${$tc('settings.discipline', 2)} - ${getCategoryName(
          form.category
        )}`"
        :options="getDisciplineOptions(form.category)"
        multiple
        @input="$forceUpdate()"
      />

      <main-select
        v-model="form.members"
        :label="$tc('tournament.competitors', 2)"
        :options="memberOptions"
        :validator="$v.form.members"
        multiple
      />

      <main-select
        v-model="form.type"
        :label="$tc('general.type')"
        :options="teamTypeOptions"
        :validator="$v.form.type"
      />

      <template v-if="$feature.registration_fee">
        <main-checkbox
          v-if="!form.alwaysPays"
          v-model="form.noPay"
          :label="$tc('club.registration_fee_free')"
        />
        <main-checkbox
          v-if="user.isAdmin && !form.noPay"
          v-model="form.alwaysPays"
          :label="$tc('club.must_pay_registration_fee')"
        />
      </template>
    </template>

    <template v-slot:footer>
      <div class="bottom-nav">
        <div class="flex" v-if="user.isAdmin">
          <span class="switch-label">{{
            $tc('tournament.show_category_based')
          }}</span>
          <el-switch v-model="showCompetitorsBasedOnSelectedCategory" />
        </div>

        <div class="flex-buttons">
          <el-button @click="$emit('close')">{{
            $tc('general.cancel')
          }}</el-button>
          <el-button type="primary" @click="save()">{{
            isEdit ? $tc('general.edit') : $tc('general.save')
          }}</el-button>
        </div>
      </div>
    </template>
  </main-drawer>
</template>

<script>
import { teamTypeOptions } from '@/common/definitions'
import errorHandler from '@/common/errorHandler.js'
import { required } from 'vuelidate/lib/validators'

export default {
  props: {
    tournament: {
      type: Object,
      required: true
    },

    club: {
      type: Object,
      default: null
    },

    team: {
      type: Object,
      default: null
    }
  },

  data () {
    return {
      form: {
        clubId: null,
        members: [],
        type: null,
        category: null,
        disciplines: {},
        noPay: null,
        alwaysPays: null
      },

      teamTypeOptions,
      showCompetitorsBasedOnSelectedCategory: false,
      clubWithMembers: null
    }
  },

  computed: {
    showClubSelect () {
      return !this.club && this.user?.isAdmin && !this.team
    },

    effectiveClub () {
      if (this.club) return this.club
      if (this.team?.club) return this.team.club
      if (this.showClubSelect && this.form.clubId) {
        return (
          this.clubWithMembers ||
          this.$store.getters['clubs/get'](this.form.clubId)
        )
      }
      return null
    },

    clubOptions () {
      const list = this.$store.getters['clubs/list'] || []
      return list
        .filter((c) => !c.isBlocked && !c.isDeleted)
        .map((c) => ({ id: c.id, text: c.name }))
    },

    isEdit () {
      return !!this.team
    },

    disciplineErrors () {
      const data = {}

      if (this.form.category) {
        data[this.form.category] = {
          required: !!this.form.disciplines[this.form.category]?.length,
          $dirty: this.$v.form.$dirty,
          $pending: this.$v.form.$pending,
          $invalid: !this.form.disciplines[this.form.category]?.length,
          $touch: () => {}
        }
      }

      return data
    },

    tournamentDisciplines () {
      return this.tournament.teamDisciplines.map((d) => d.discipline)
    },

    categoryOptions () {
      const categoryIdsObject = {}
      this.tournamentDisciplines.forEach((d) => {
        d.disciplineCategories.forEach((cat) => {
          categoryIdsObject[cat.categoryId] = true
        })
      })

      const tournamentCategories =
        this.allCategories.filter(
          (category) =>
            !category.isArchived &&
            Object.keys(categoryIdsObject).includes(category.id + '')
        ) || []

      return tournamentCategories.map((category) => ({
        id: category.id,
        text: category.name
      }))
    },

    allCategories () {
      return this.$store.state.categories.data
    },

    memberOptions () {
      let filteredMembers =
        this.members?.filter((member) => member.isActive) || []

      const sortedCategories = this.allCategories
        .filter((category) => !category.isArchived)
        .sort((a, b) => a.ageFrom - b.ageFrom)
      const selectedCategoryIndex = sortedCategories.findIndex(
        (category) => category.id + '' === this.form.category + ''
      )
      const previousCategory = sortedCategories[selectedCategoryIndex - 1]

      if (
        this.form.category &&
        (this.showCompetitorsBasedOnSelectedCategory || !this.user.isAdmin)
      ) {
        const selectedCategory = this.allCategories.find(
          (category) => category.id + '' === this.form.category + ''
        )
        if (
          selectedCategory &&
          (selectedCategory.ageFrom || selectedCategory.ageTo)
        ) {
          const catMinAge = selectedCategory.ageFrom || 0
          const catMaxAge = selectedCategory.ageTo || 100
          const youngerMinAge = previousCategory?.ageFrom ?? catMinAge

          const hasSelectedYoungerMember = this.form.members.some(
            (memberId) => {
              const m = this.members.find((member) => member.id === memberId)
              if (!m?.born) return false
              const age = this.calculateAge(m.born)
              return age >= youngerMinAge && age < catMinAge
            }
          )

          filteredMembers = filteredMembers.filter((member) => {
            if (this.form.members.includes(member.id)) return true
            if (!member.born) return true
            const age = this.calculateAge(member.born)

            if (age >= catMinAge && age <= catMaxAge) return true

            if (
              !hasSelectedYoungerMember &&
              age >= youngerMinAge &&
              age < catMinAge
            ) {
              return true
            }

            return false
          })
        }
      }

      return filteredMembers.map((member) => ({
        id: member.id,
        text: member.firstName + ' ' + member.lastName
      }))
    },

    members () {
      return this.effectiveClub?.members || []
    },

    user () {
      return this.$store.state.auth.user
    }
  },

  watch: {
    'form.category': {
      handler (categoryId) {
        if (categoryId && !this.form.disciplines[categoryId]) {
          this.form.disciplines[categoryId] = []
          this.$forceUpdate()
        }
      }
    },
    showCompetitorsBasedOnSelectedCategory (value) {
      localStorage.setItem(
        'showCompetitorsBasedOnSelectedCategory',
        value.toString()
      )
    }
  },

  created () {
    const storedValue = localStorage.getItem(
      'showCompetitorsBasedOnSelectedCategory'
    )
    this.showCompetitorsBasedOnSelectedCategory = storedValue === 'true'
    if (this.showClubSelect) {
      this.$store.dispatch('clubs/load')
    }

    if (this.team) {
      const firstCategory = this.team.categories?.[0]
      this.form = {
        type: this.team.type,
        noPay: this.team.noPay,
        alwaysPays: this.team.alwaysPays,
        members: [
          this.team.competitorId,
          this.team.competitorTwoId,
          this.team.competitorThreeId,
          this.team.competitorFourId
        ].filter((m) => !!m),
        category: firstCategory?.categoryId || null,
        disciplines: this.team.categories?.reduce((acc, i) => {
          acc[i.categoryId] = i.disciplines?.map((d) => d.disciplineId)
          return acc
        }, {})
      }
    }
  },

  methods: {
    onClubChange () {
      this.form.members = []
      this.form.category = null
      this.form.disciplines = {}
      this.form.type = null
      this.loadClubMembers(this.form.clubId)
    },

    async loadClubMembers (clubId) {
      if (!clubId) {
        this.clubWithMembers = null
        return
      }
      try {
        await this.$store.dispatch('clubs/get', clubId)
        this.clubWithMembers = this.$store.getters['clubs/get'](clubId)
      } catch (e) {
        this.clubWithMembers = null
        console.error(e)
      }
    },

    calculateAge (birthDate) {
      if (!birthDate) return null

      const birth = new Date(birthDate)
      const today = new Date()
      return today.getFullYear() - birth.getFullYear()
    },

    getDisciplineOptions (categoryId) {
      const tournamentDisciplines = this.tournamentDisciplines.map(
        (tournamentDiscipline) => ({
          ...tournamentDiscipline,
          categoryIds: tournamentDiscipline.disciplineCategories.map(
            (category) => category.categoryId
          )
        })
      )

      const disciplines = tournamentDisciplines.filter((discipline) =>
        discipline.categoryIds.includes(categoryId)
      )

      return disciplines.map((discipline) => ({
        id: discipline.id,
        text: discipline.name
      }))
    },

    getCategoryName (categoryId) {
      return this.categoryOptions.find((category) => category.id === categoryId)
        ?.text
    },

    checkDisciplines () {
      return (
        !!this.form.category &&
        !!this.form.disciplines?.[this.form.category]?.length
      )
    },

    async save () {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) return

      if (![2, 3, 4].includes(this.form.members?.length)) {
        return this.$message.error(
          this.$tc('messages.error_number_of_selected_competitors')
        )
      }

      if (!this.checkDisciplines()) {
        return this.$message.error(
          this.$tc('messages.error_select_at_least_one_discipline')
        )
      }

      const payload = {
        type: this.form.type,
        noPay: this.form.noPay,
        alwaysPays: this.form.alwaysPays,
        memberIds: this.form.members,
        categories: [
          {
            id: this.form.category,
            disciplines: this.form.disciplines[this.form.category] || []
          }
        ]
      }

      this.loading = true
      try {
        let tournament
        if (this.isEdit) {
          tournament = await this.$store.dispatch('tournaments/updateTeam', {
            id: this.team.id,
            payload
          })
        } else {
          tournament = await this.$store.dispatch('tournaments/registerTeam', {
            id: this.tournament.id,
            payload
          })
        }
        this.$message.success(
          this.isEdit
            ? this.$tc('messages.success_updated_team')
            : this.$tc('messages.success_registered_team')
        )
        this.$emit(this.isEdit ? 'edit' : 'add', tournament)
      } catch (error) {
        errorHandler(error)
      } finally {
        this.loading = false
      }
    }
  },

  validations () {
    const rules = {
      form: {
        members: { required, minLength: 2, maxLength: 4 },
        type: { required },
        category: { required },
        disciplines: {}
      }
    }

    if (this.form.category) {
      rules.form.disciplines[this.form.category] = { required }
    }

    return rules
  }
}
</script>

<style lang="scss" scoped>
.bottom-nav {
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 20px;
}

.flex {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.switch-label {
  font-size: 14px;
  color: #606266;
}
</style>
